// DeterminarPagos.js - Versión con guardado inmediato
import { Box, Dialog, DialogTitle, Typography, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import React, { useEffect, useState } from 'react';
import { PrefacturaCard } from './components/PrefacturaCard';
import { PagosTable } from './components/PagosTable';
import { ResumenPagos } from './components/ResumenPagos';
import { BotonesAccion } from './components/BotonesAccion';
import { AgregarPagoModal } from './components/AgregarPagoModal';
import { BotonAgregarPago } from './components/BotonAgregarPago';
import { usePagos } from './hooks/usePagos';
import { usePrefactura } from './hooks/usePrefactura';
import { useMediosPago } from './hooks/useMediosPago';
import { formatImporte } from './utils/formatters';

function DeterminarPagos(props) {
  const [determinarPago, setDeterminarPago] = useState(props.determinarPago);
  const [editandoId, setEditandoId] = useState(null);
  const [nuevoImporte, setNuevoImporte] = useState('');
  const [modalAgregarPagoOpen, setModalAgregarPagoOpen] = useState(false);

  const {
    pagosPendientes,
    loading,
    totalPagos,
    obtenerPagosPendientes,
    actualizarPago,  // Cambiado
    agregarNuevoPago,
    guardarPedido
  } = usePagos(props.pedido.id);

  const {
    calculando: calculandoPrefactura,
    error: errorPrefactura,
    totalNeto,
    ivaCalculado,
    percepciones,
    totalFinal,
    calcularPreFactura
  } = usePrefactura(props.pedido.id, props.pedido.a_consfinal || 0);

  const { mediosPago } = useMediosPago("envasado");

  const handleClose = () => {
    setDeterminarPago(!determinarPago);
    if (props.onClose) {
      props.onClose();
    }
  };

  const handleStartEdit = (pago) => {
    setEditandoId(pago.id);
    setNuevoImporte(pago.importe.toString());
  };

  const handleCancelEdit = () => {
    setEditandoId(null);
    setNuevoImporte('');
  };

  // Guardado inmediato
  const handleSaveEdit = async (id, importeNumerico) => {
    if (importeNumerico < 0) {
      alert('El importe no puede ser menor a 0');
      return;
    }
    
    if (isNaN(importeNumerico)) {
      alert('Ingrese un importe válido');
      return;
    }

    const success = await actualizarPago(id, importeNumerico);
    if (success) {
      await calcularPreFactura();
    }
    setEditandoId(null);
    setNuevoImporte('');
  };

  const handleImporteChange = (e) => {
    let value = e.target.value;
    value = value.replace(',', '.');
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setNuevoImporte(value);
    }
  };

  const handleAgregarPago = async (nuevoPago) => {
    await agregarNuevoPago(nuevoPago);
    await calcularPreFactura();
    setModalAgregarPagoOpen(false);
  };

  const handleFinalizar = async () => {
    const diferenciaPendiente = totalFinal - totalPagos;
    
    if (Math.abs(diferenciaPendiente) > 0.01) {
      alert(`El monto total de los pagos ($${formatImporte(totalPagos)}) no coincide con el total de la factura ($${formatImporte(totalFinal)}). Diferencia: $${formatImporte(Math.abs(diferenciaPendiente))}`);
      return;
    }
    
    const success = await guardarPedido(props.pedido);
    if (success) {
      handleClose();
    }
  };

  useEffect(() => {
    const cargarDatos = async () => {
      await obtenerPagosPendientes();
      await calcularPreFactura();
    };
    cargarDatos();
  }, []);

  const diferenciaPendiente = totalFinal - totalPagos;
  const diferenciaFaltante = totalFinal - totalPagos > 0 ? totalFinal - totalPagos : 0;

  return (
    <>
      <Dialog fullWidth open={determinarPago} onClose={handleClose} maxWidth="md">
        <Box justifyContent="center" backgroundColor="#1F618D">
          <DialogTitle>
            <Typography fontSize={20} align="center" color="#ffffff">
              Determinar pago del pedido N°{props.pedido.id}
            </Typography>
          </DialogTitle>
        </Box>

        <Box p={3}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Importante:
            </Typography>
            <Typography variant="body2">
              Al guardar, el pedido pasará a estar pendiente de facturación.
              Podes agregar nuevos medios de pago usando el botón "Agregar Pago".
            </Typography>
          </Alert>

          <Box display="flex" justifyContent="flex-end">
            <BotonAgregarPago 
              onClick={() => setModalAgregarPagoOpen(true)} 
              disabled={loading}
            />
          </Box>

          {pagosPendientes.length > 0 ? (
            <PagosTable
              pagos={pagosPendientes}
              editandoId={editandoId}
              nuevoImporte={nuevoImporte}
              loading={loading}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onImporteChange={handleImporteChange}
            />
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No hay pagos pendientes para este pedido
              </Typography>
            </Box>
          )}

          <ResumenPagos 
            totalPagos={totalPagos} 
            totalFactura={totalFinal} 
          />

          <BotonesAccion
            onFinalizar={handleFinalizar}
            onCancelar={handleClose}
            disabled={loading}
            diferenciaPendiente={diferenciaPendiente}
          />
        </Box>
      </Dialog>

      <AgregarPagoModal
        open={modalAgregarPagoOpen}
        onClose={() => {
          setModalAgregarPagoOpen(false);
          obtenerPagosPendientes();
        }}
        mediosPago={mediosPago}
        pedidoId={props.pedido.id}
        onPagoAgregado={handleAgregarPago}
        diferenciaFaltante={diferenciaFaltante}
        uuid={props.pedido.uuid_pedido}
      />
    </>
  );
}

export default DeterminarPagos;